import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  OnChanges,
  SimpleChanges,
  inject,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';
import { SignupService, ErrorResponse } from '../../services/signup.service';
import { UtmService } from '../../services/utm.service';
import { AnalyticsService } from '../../services/analytics.service';
import { SignupRequest } from '../../models/signup';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { CheckboxModule } from 'primeng/checkbox';

@Component({
  selector: 'app-signup-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    CheckboxModule,
  ],
  templateUrl: './signup-modal.html',
  styleUrl: './signup-modal.scss',
  animations: [
    trigger('backdrop', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0 })),
      ]),
    ]),
    trigger('panel', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'scale(0.85)',
        }),
        animate(
          '350ms cubic-bezier(0.34, 1.56, 0.64, 1)',
          style({
            opacity: 1,
            transform: 'scale(1)',
          })
        ),
      ]),
      transition(':leave', [
        animate(
          '200ms ease-in',
          style({
            opacity: 0,
            transform: 'scale(0.95)',
          })
        ),
      ]),
    ]),
  ],
})
export class SignupModal implements OnChanges {
  @Input() isOpen = false;
  @Output() close = new EventEmitter<void>();

  readonly form: FormGroup;
  readonly submitting = signal(false);
  readonly success = signal(false);
  readonly duplicate = signal(false);
  readonly errorMessage = signal<string | null>(null);

  readonly showForm = computed(() => !this.success());

  private readonly utmService = inject(UtmService);
  private readonly analytics  = inject(AnalyticsService);

  constructor(
    private readonly fb: FormBuilder,
    private readonly signupService: SignupService
  ) {
    this.form = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      nerdOutAbout: ['', [Validators.required]],
      agreeToEmails: [true, [Validators.requiredTrue]],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const isOpen = changes['isOpen'];
    if (isOpen?.currentValue) {
      document.body.style.overflow = 'hidden';
      this.success.set(false);
      this.duplicate.set(false);
      this.errorMessage.set(null);
      this.form.reset({ agreeToEmails: true });
    } else if (isOpen?.previousValue && !isOpen?.currentValue) {
      document.body.style.overflow = '';
    }
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    if (this.isOpen) this.emitClose();
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-backdrop')) {
      this.emitClose();
    }
  }

  emitClose(): void {
    this.close.emit();
  }

  onSubmit(): void {
    if (this.form.invalid || this.submitting()) return;
    const value = this.form.getRawValue();
    const request: SignupRequest = {
      email: (value.email ?? '').trim(),
      firstName: (value.firstName ?? '').trim(),
      lastName: (value.lastName ?? '').trim(),
      interest: (value.nerdOutAbout ?? '').trim(),
    };
    this.submitting.set(true);
    this.errorMessage.set(null);
    const utms = this.utmService.get();
    this.signupService
      .signup(request, utms)
      .subscribe({
        next: (response) => {
          this.submitting.set(false);
          this.success.set(true);
          this.duplicate.set(!!response?.duplicate);
          this.analytics.identify(request.email, { utm_source: utms.utm_source });
          this.analytics.capture('mailing_list_signup', { ...utms, duplicate: !!response?.duplicate });
        },
        error: (err: ErrorResponse) => {
          this.submitting.set(false);
          this.errorMessage.set(
            this.getUserFriendlyErrorMessage(err)
          );
        },
      });
  }

  onCancel(): void {
    this.emitClose();
  }

  private getUserFriendlyErrorMessage(error: ErrorResponse): string {
    if (error?.message && error.message !== 'An unexpected error occurred. Please try again later.') {
      if (error.error === 'ValidationError' && error.details) {
        const parts: string[] = [];
        Object.keys(error.details).forEach((field) => {
          error.details![field].forEach((msg) => parts.push(`${field}: ${msg}`));
        });
        if (parts.length) return parts.join('\n');
      }
      return error.message;
    }
    switch (error?.error) {
      case 'ValidationError':
        return 'Please check your information and make sure all required fields are filled correctly.';
      case 'NetworkError':
        return 'Unable to connect. Please check your internet connection and try again.';
      case 'RateLimitError':
        return 'Too many signup attempts. Please wait a moment and try again.';
      case 'TimeoutError':
        return 'The request took too long. Please try again.';
      default:
        return 'There was a problem completing your signup. Please try again.';
    }
  }
}
